import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { catchError, of } from 'rxjs';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { PlatformService } from '../platform.service';
import { CommonService } from '../common.service';
import { ConfigService } from '../config.service';
import { PortalCategory, DEFAULT_CATEGORIES, THEMATIC_TAG_COUNT_PLACEHOLDER } from '../portal-categories';

export { THEMATIC_TAG_COUNT_PLACEHOLDER };

// Pure (no HttpClient/TestBed needed to test it): maps a thematic_tags singleFacet
// response — expected shape {"PPP": 438074, "WHO": 3894245, ...} — onto categories
// by their thematicTag. `response` is null when the API call itself failed.
export function mapThematicTagCounts(categories: PortalCategory[], response: Record<string, number> | null): PortalCategory[] {
  return categories.map(category => {
    const rawCount = response ? response[category.thematicTag] : undefined;
    const entriesCount = typeof rawCount === 'number' && Number.isFinite(rawCount)
      ? rawCount.toLocaleString('en-US')
      : THEMATIC_TAG_COUNT_PLACEHOLDER;
    return { ...category, entriesCount };
  });
}

@Component({
    selector: 'app-browse-data-carousel',
    templateUrl: './browse-data-carousel.component.html',
    styleUrls: ['./browse-data-carousel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BrowseDataCarouselComponent implements OnInit, OnDestroy {
  @Input() categories: PortalCategory[] = DEFAULT_CATEGORIES;

  pageIndex = 0;
  trackOffset = 0;
  dots: number[] = [0];

  private step = 0;
  private visibleCount = 1;
  private maxCardIndex = 0;

  // Mirrors the breakpoints in browse-data-carousel.component.css / portal-card.component.css.
  // This carousel is a fixed 3-breakpoint layout, not a fluid one — card width and visible
  // count are fully determined by viewport width, so there's no need to measure rendered DOM
  // (offsetWidth via ResizeObserver) to know them. Previously this was computed by measuring
  // the DOM after view init, retried up to 10 times over ~2s to outrun layout/hydration timing
  // that was sometimes still settling on first load — a race that could still be lost on a
  // slow/cold first load, leaving the pagination dots stuck wrong (AFPDBE-1331: reported as
  // "1 dot instead of 2 until refresh" on the test env). matchMedia is synchronous and doesn't
  // depend on rendering having finished, so that race no longer exists.
  private static readonly TABLET_QUERY = '(max-width: 64.625em)';
  private static readonly MOBILE_QUERY = '(max-width: 39.9375em)';
  private static readonly GAP = 16;
  private static readonly LAYOUTS = {
    desktop: { cardWidth: 535, visibleCount: 2 },
    tablet: { cardWidth: 300, visibleCount: 2 },
    mobile: { cardWidth: 240, visibleCount: 1 },
  };

  private mobileMql: MediaQueryList | null = null;
  private tabletMql: MediaQueryList | null = null;
  private readonly onBreakpointChange = () => this.zone.run(() => this.recalculate());

  constructor(
    public gaService: GoogleAnalyticsService,
    private platform: PlatformService,
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    private commonService: CommonService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    // Only auto-fetch when the caller left `categories` at its default — an explicit
    // [categories] input (tests, or any future consumer with fixed data) opts out.
    // Browser-only: skipped during SSR so the server render never depends on this
    // endpoint's uptime/latency; the client swaps in live counts after hydration.
    if (this.platform.isBrowserPlatform() && this.categories === DEFAULT_CATEGORIES) {
      this.fetchEntryCounts();
    }

    if (!this.platform.isBrowserPlatform() || typeof window.matchMedia !== 'function') {
      this.recalculate(); // SSR, or no matchMedia support: fall back to the desktop layout
      return;
    }

    this.mobileMql = window.matchMedia(BrowseDataCarouselComponent.MOBILE_QUERY);
    this.tabletMql = window.matchMedia(BrowseDataCarouselComponent.TABLET_QUERY);
    // matchMedia 'change' fires outside Angular's zone, so without this zone.run()
    // markForCheck() inside recalculate() would schedule a check Angular never flushes.
    this.mobileMql.addEventListener('change', this.onBreakpointChange);
    this.tabletMql.addEventListener('change', this.onBreakpointChange);
    this.recalculate();
  }

  private fetchEntryCounts(): void {
    const apiKey = this.configService.getConfig().apiKey;
    const auth = apiKey ? `&key=${apiKey}` : '';
    const url = `${this.configService.getConfig().searchApiUrl}?q=*:*&type=singleFacet&facet=thematic_tags${auth}`;

    this.commonService.getApiData(url)
      .pipe(
        catchError(error => {
          console.error('Error fetching thematic_tags entry counts:', error);
          return of(null);
        })
      )
      .subscribe(response => {
        this.categories = mapThematicTagCounts(this.categories, response);
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.mobileMql?.removeEventListener('change', this.onBreakpointChange);
    this.tabletMql?.removeEventListener('change', this.onBreakpointChange);
  }

  prev(): void {
    this.goToPage(this.pageIndex - 1);
  }

  next(): void {
    this.goToPage(this.pageIndex + 1);
  }

  goToPage(page: number): void {
    this.setPage(page);
    this.gaService.eventEmitter('portal_carousel_navigate', 'portal_views', 'click', String(this.pageIndex), undefined);
  }

  onCardFocus(cardIndex: number): void {
    // Keeps a keyboard-tabbed card in view: the track uses overflow:hidden + translateX
    // rather than native scroll, so without this a focused off-screen card would be
    // clipped and invisible even though it has focus. Jump to whichever page contains it.
    this.setPage(Math.floor(cardIndex / this.visibleCount));
  }

  private setPage(page: number): void {
    const lastPage = this.dots.length - 1;
    this.pageIndex = Math.min(Math.max(page, 0), lastPage);
    const cardIndex = Math.min(this.pageIndex * this.visibleCount, this.maxCardIndex);
    this.trackOffset = cardIndex * this.step;
  }

  private recalculate(): void {
    const layout = this.mobileMql?.matches
      ? BrowseDataCarouselComponent.LAYOUTS.mobile
      : this.tabletMql?.matches
        ? BrowseDataCarouselComponent.LAYOUTS.tablet
        : BrowseDataCarouselComponent.LAYOUTS.desktop;

    this.step = layout.cardWidth + BrowseDataCarouselComponent.GAP;
    this.visibleCount = layout.visibleCount;
    this.maxCardIndex = Math.max(0, this.categories.length - this.visibleCount);

    // One dot per page (a page = however many cards are visible at once), not one per
    // card — a 1-dot-per-card scheme leaves trailing dots that land on the same scroll
    // position as the one before them (dead clicks) whenever fewer cards are visible
    // than exist.
    const pageCount = Math.max(1, Math.ceil(this.categories.length / this.visibleCount));
    if (pageCount !== this.dots.length) {
      this.dots = Array.from({ length: pageCount }, (_, i) => i);
    }

    this.setPage(this.pageIndex);
    this.cd.markForCheck();
  }
}
